import { getCustomContactPropertiesWithoutNested } from '@/configs/custom-contact-properties';

export interface ConsentChannel {
  id: string;
  identifierField: string;
  consentField: string;
  implicitAllowed: boolean;
  checkboxAlwaysRequired: boolean;
  disclosureRequired: boolean;
}

export type ConsentMode = 'explicit' | 'implicit' | 'not_configured' | 'not_applicable' | 'already_collected';

export interface DetectedConsent {
  channelId: string;
  label: string;
  mode: ConsentMode;
  identifierPresent: boolean;
  consentFieldPresent: boolean;
  hasDisclosure: boolean;
  disclosureRequired: boolean;
  checkboxRequiredByAccountCreation: boolean;
  disclosureAdvisory: boolean;
}

export interface ConditionalRequiredField {
  field: string;
  condition: 'platformRequiresPassword';
}

export interface FormAction {
  id: string;
  name: string;
  description: string;
  icon: string;
  requiredFields: string[];
  optionalFields: string[];
  conditionalRequiredFields?: ConditionalRequiredField[];
  identificationFields?: string[];
  consentChannels?: ConsentChannel[];
  priority: number;
  color: string;
  autoActivate?: boolean;
}

/**
 * Platforms that do NOT require a password for account creation.
 */
export const PLATFORMS_WITHOUT_PASSWORD = ['shopify'];

export interface CustomSchemaAction extends FormAction {
  schemaId: string;
  schemaName: string;
  schemaDescription?: string;
  fieldLabels?: Record<string, string>;
}

/**
 * Consentement au suivi des ouvertures (pixels CNIL) collecté de façon IMPLICITE
 * via le bouton d'envoi (aucune case visiteur). Adossé au canal email.
 */
export interface TrackingConsentInfo {
  // Applicable = un email est collecté ET aucun widget case de consentement n'est présent
  applicable: boolean;
  // Activé = applicable ET non désactivé par le marchand (défaut ON)
  enabled: boolean;
  // Le widget Divulgation couvre-t-il déjà le canal email ?
  disclosureCovered: boolean;
}

export interface FormActionStatus {
  action: FormAction;
  isAvailable: boolean;
  isEnabled: boolean;
  missingFields: string[];
  availableFields: string[];
  optionalAvailableFields: string[];
  customPropertiesBySchema?: Record<string, Array<{ property: string; label?: string; isInherited: boolean; sourceTemplate?: string }>>;
  inheritedNativeProperties?: Map<string, string>;
  detectedConsents?: DetectedConsent[];
  trackingConsent?: TrackingConsentInfo;
}

export const CONSENT_CHANNEL_LABELS: Record<string, string> = {
  email: 'Email marketing',
  sms: 'SMS marketing',
  whatsapp: 'WhatsApp',
};

export const NATIVE_FORM_ACTIONS: FormAction[] = [
  {
    id: 'consent',
    name: 'Consentements',
    description: 'Active les consentements marketing',
    icon: 'fas fa-shield-check',
    requiredFields: [],
    optionalFields: [],
    identificationFields: ['email', 'phone_number'],
    consentChannels: [
      { id: 'email', identifierField: 'email', consentField: 'consent_email', implicitAllowed: true, checkboxAlwaysRequired: false, disclosureRequired: false },
      { id: 'sms', identifierField: 'phone_number', consentField: 'consent_sms', implicitAllowed: true, checkboxAlwaysRequired: false, disclosureRequired: false },
      { id: 'whatsapp', identifierField: 'phone_number', consentField: 'consent_whatsapp', implicitAllowed: false, checkboxAlwaysRequired: true, disclosureRequired: false },
    ],
    priority: 1,
    color: '#0288D1',
    autoActivate: true,
  },
  {
    id: 'account_creation',
    name: 'Création de compte',
    description: 'Création d\'un compte utilisateur avec authentification',
    icon: 'fas fa-user-plus',
    requiredFields: ['email', 'first_name', 'last_name'],
    conditionalRequiredFields: [
      { field: 'password', condition: 'platformRequiresPassword' },
    ],
    optionalFields: ['phone_number', 'birthday'],
    priority: 2,
    color: '#6c757d',
    autoActivate: false,
  },
];

/**
 * Generate form actions for custom data storage, one action per schema
 */
export const generateCustomSchemaActions = async (): Promise<CustomSchemaAction[]> => {
  const customSchemas = await getCustomContactPropertiesWithoutNested();

  return customSchemas.map((schema, index) => {
    const fieldLabels: Record<string, string> = {};
    schema.properties.forEach((prop) => {
      if (prop.label) {
        fieldLabels[prop.name] = prop.label;
      }
    });

    return {
      id: `custom_data_storage_${schema.schemaId}`,
      name: schema.schemaName,
      schemaId: schema.schemaId,
      schemaName: schema.schemaName,
      schemaDescription: schema.schemaDescription,
      fieldLabels,
      icon: 'fas fa-database',
      description: `Enregistrer les données du schéma ${schema.schemaName}`,
      requiredFields: schema.requiredProperties.map((prop) => `${schema.schemaId}_${prop}`),
      optionalFields: schema.properties
        .filter((prop) => !prop.required)
        .map((prop) => prop.value),
      priority: 4 + index,
      color: '#8b5cf6',
    };
  });
};

// Cache for form actions
let cachedFormActions: FormAction[] | null = null;

/**
 * Get all form actions (native + custom schemas)
 */
export const getFormActions = async (): Promise<FormAction[]> => {
  if (cachedFormActions) {
    return cachedFormActions;
  }

  const customSchemaActions = await generateCustomSchemaActions();
  cachedFormActions = [...NATIVE_FORM_ACTIONS, ...customSchemaActions];

  return cachedFormActions;
};

/**
 * Invalidate form actions cache (call when schemas are updated)
 */
export const invalidateFormActionsCache = () => {
  cachedFormActions = null;
};

/**
 * Legacy export for backward compatibility
 * @deprecated Use getFormActions() instead
 */
export const FORM_ACTIONS = NATIVE_FORM_ACTIONS;
